import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { runPricingEngine, generateAIInsights, saveScenario, parseMISMOForPricing, OCCUPANCY_TYPES, PROPERTY_TYPES, LOAN_PURPOSES, LOAN_TYPES } from '../../services/pricingService';
import { validateMISMOFile } from '../../services/loanService';
import { createNewLoan } from '../../services/loanService';
import FloatingAIAssistant from '../../components/broker/FloatingAIAssistant';

const ScenarioPricerPage = () => {
  const navigate = useNavigate();
  const selectedDetailsRef = useRef(null);
  
  // Pricing inputs
  const [inputs, setInputs] = useState({
    creditScore: 740,
    occupancy: OCCUPANCY_TYPES.PRIMARY,
    propertyState: 'CA',
    propertyType: PROPERTY_TYPES.SINGLE_FAMILY,
    units: 1,
    firstTimeBuyer: false,
    loanPurpose: LOAN_PURPOSES.PURCHASE,
    loanType: LOAN_TYPES.MORTGAGE,
    loanAmount: 450000,
    propertyValue: 500000,
    cashOutAmount: 0,
    term: 360,
    amortizationType: 'Fixed',
    lockPeriod: 30
  });
  
  // State management
  const [pricingResults, setPricingResults] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [comparedScenarios, setComparedScenarios] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [mismoFile, setMismoFile] = useState(null);
  const [mismoXmlText, setMismoXmlText] = useState(null);
  const [sortBy, setSortBy] = useState('interestRate');
  const [filterEligible, setFilterEligible] = useState(false);
  const [isAIPanelLocked, setIsAIPanelLocked] = useState(false);
  
  // Calculated values
  const ltv = inputs.propertyValue > 0 ? ((inputs.loanAmount / inputs.propertyValue) * 100).toFixed(2) : 0;
  const cltv = ltv; // Simplified - would include second liens in real scenario

  // Auto-scroll to selected product details
  useEffect(() => {
    if (selectedScenario && selectedDetailsRef.current) {
      setTimeout(() => {
        selectedDetailsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedScenario]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleRunPricing = async () => {
    setLoading(true);
    setPricingResults([]);
    setAiInsights(null);
    setSelectedScenario(null);
    
    try {
      const results = await runPricingEngine(inputs);
      setPricingResults(results);
      
      // Generate AI insights
      const insights = generateAIInsights(inputs, results);
      setAiInsights(insights);
    } catch (error) {
      console.error('Pricing error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMISMOImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const validation = await validateMISMOFile(file);
      if (validation.valid) {
        const pricingInputs = parseMISMOForPricing(validation.xmlDoc);
        setInputs(pricingInputs);
        setMismoFile(file);
        setMismoXmlText(validation.xmlText);
        alert('MISMO file imported successfully! Pricing inputs populated. Click "Run Pricing" to see results.');
      }
    } catch (error) {
      alert(`Import failed: ${error.details || error.message}`);
    }
  };

  const handleSaveScenario = async () => {
    if (!selectedScenario || !scenarioName) return;
    
    const saved = await saveScenario({
      inputs: inputs,
      scenario: selectedScenario,
      mismoFile: mismoFile?.name || null
    }, scenarioName);
    
    setSavedScenarios(prev => [...prev, saved]);
    setShowSaveModal(false);
    setScenarioName('');
    alert(`Scenario "${scenarioName}" saved successfully!`);
  };

  const toggleCompare = (scenario) => {
    if (comparedScenarios.find(s => s.id === scenario.id)) {
      setComparedScenarios(comparedScenarios.filter(s => s.id !== scenario.id));
    } else {
      if (comparedScenarios.length < 3) {
        setComparedScenarios([...comparedScenarios, scenario]);
      } else {
        alert('Maximum 3 scenarios can be compared at once');
      }
    }
  };

  const handleConvertToLoan = async (useManual) => {
    if (!selectedScenario) return;
    
    if (useManual) {
      // Create loan with selected pricing, manual entry for borrower info
      const loanData = {
        loanPurpose: inputs.loanPurpose,
        loanType: inputs.loanType,
        loanAmount: inputs.loanAmount,
        property: {
          state: inputs.propertyState,
          value: inputs.propertyValue
        },
        selectedPricing: {
          productName: selectedScenario.productName,
          programType: selectedScenario.programType,
          interestRate: selectedScenario.interestRate,
          term: selectedScenario.term,
          monthlyPayment: selectedScenario.monthlyPayment,
          lockedFromScenario: true
        },
        mismo: {
          version: '3.4',
          imported: false,
          pricedScenario: true
        }
      };
      
      const newLoan = await createNewLoan(loanData);
      navigate(`/broker/loans/${newLoan.id}`);
    } else {
      // MISMO merge - use imported XML + selected pricing
      if (!mismoXmlText) {
        alert('No MISMO file available. Please import a MISMO file first.');
        return;
      }
      
      const loanData = {
        loanPurpose: inputs.loanPurpose,
        loanType: inputs.loanType,
        loanAmount: inputs.loanAmount,
        borrower: {
          firstName: 'From',
          lastName: 'MISMO'
        },
        property: {
          state: inputs.propertyState,
          value: inputs.propertyValue
        },
        selectedPricing: {
          productName: selectedScenario.productName,
          programType: selectedScenario.programType,
          interestRate: selectedScenario.interestRate,
          term: selectedScenario.term,
          monthlyPayment: selectedScenario.monthlyPayment,
          lockedFromScenario: true
        },
        originalXML: mismoXmlText,
        mismo: {
          version: '3.4',
          imported: true,
          pricedScenario: true
        }
      };
      
      const newLoan = await createNewLoan(loanData);
      navigate(`/broker/loans/${newLoan.id}`);
    }
  };

  const getSortedResults = () => {
    let results = [...pricingResults];
    
    if (filterEligible) {
      results = results.filter(r => r.eligible);
    }
    
    results.sort((a, b) => {
      if (!a.eligible && b.eligible) return 1;
      if (a.eligible && !b.eligible) return -1;
      
      if (sortBy === 'interestRate') {
        return (a.interestRate || 999) - (b.interestRate || 999);
      } else if (sortBy === 'monthlyPayment') {
        return (a.monthlyPayment || 999999) - (b.monthlyPayment || 999999);
      } else if (sortBy === 'productName') {
        return (a.productName || '').localeCompare(b.productName || '');
      }
      return 0;
    });
    
    return results;
  };

  return (
    <>
      <div 
        className="page-header d-print-none mb-3"
        style={{
          marginRight: isAIPanelLocked ? '420px' : '0',
          transition: 'margin-right 0.3s ease'
        }}
      >
        <div className="row g-2 align-items-center">
          <div className="col">
            <h2 className="page-title">
              <i className="ti ti-calculator me-2"></i>
              Scenario Pricer
            </h2>
            <div className="text-secondary mt-1">
              Fast pricing and comparison tool - no loan required
            </div>
          </div>
          <div className="col-auto">
            <div className="btn-list">
              <label className="btn btn-success">
                <i className="ti ti-file-import me-1"></i>
                Import MISMO
                <input
                  type="file"
                  accept=".xml"
                  onChange={handleMISMOImport}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div 
        className="row row-deck row-cards"
        style={{
          marginRight: isAIPanelLocked ? '420px' : '0',
          transition: 'margin-right 0.3s ease'
        }}
      >
          {/* Input Form */}
          <div className="col-lg-3">
            <div className="card sticky-top" style={{ top: '1rem' }}>
              <div className="card-header">
                <h3 className="card-title">Pricing Inputs</h3>
              </div>
              <div className="card-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {/* Borrower / Credit */}
                <h4 className="mb-3">Borrower / Credit</h4>
                
                <div className="mb-3">
                  <label className="form-label">Credit Score (Representative)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={inputs.creditScore}
                    onChange={(e) => handleInputChange('creditScore', parseInt(e.target.value))}
                    min="300"
                    max="850"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Occupancy</label>
                  <select
                    className="form-select"
                    value={inputs.occupancy}
                    onChange={(e) => handleInputChange('occupancy', e.target.value)}
                  >
                    <option value={OCCUPANCY_TYPES.PRIMARY}>Primary Residence</option>
                    <option value={OCCUPANCY_TYPES.SECOND}>Second Home</option>
                    <option value={OCCUPANCY_TYPES.INVESTMENT}>Investment Property</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Property State</label>
                  <select
                    className="form-select"
                    value={inputs.propertyState}
                    onChange={(e) => handleInputChange('propertyState', e.target.value)}
                  >
                    <option value="CA">California</option>
                    <option value="TX">Texas</option>
                    <option value="FL">Florida</option>
                    <option value="NY">New York</option>
                    <option value="PA">Pennsylvania</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Property Type</label>
                  <select
                    className="form-select"
                    value={inputs.propertyType}
                    onChange={(e) => handleInputChange('propertyType', e.target.value)}
                  >
                    <option value={PROPERTY_TYPES.SINGLE_FAMILY}>Single Family</option>
                    <option value={PROPERTY_TYPES.CONDO}>Condo</option>
                    <option value={PROPERTY_TYPES.TOWNHOUSE}>Townhouse</option>
                    <option value={PROPERTY_TYPES.MULTI_FAMILY_2}>2-Unit</option>
                    <option value={PROPERTY_TYPES.MULTI_FAMILY_3}>3-Unit</option>
                    <option value={PROPERTY_TYPES.MULTI_FAMILY_4}>4-Unit</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">First-Time Buyer</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={inputs.firstTimeBuyer}
                      onChange={(e) => handleInputChange('firstTimeBuyer', e.target.checked)}
                    />
                  </div>
                </div>

                <hr className="my-4" />

                {/* Loan Structure */}
                <h4 className="mb-3">Loan Structure</h4>

                <div className="mb-3">
                  <label className="form-label">Loan Purpose</label>
                  <select
                    className="form-select"
                    value={inputs.loanPurpose}
                    onChange={(e) => handleInputChange('loanPurpose', e.target.value)}
                  >
                    <option value={LOAN_PURPOSES.PURCHASE}>Purchase</option>
                    <option value={LOAN_PURPOSES.REFINANCE}>Refinance</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Loan Type</label>
                  <select
                    className="form-select"
                    value={inputs.loanType}
                    onChange={(e) => handleInputChange('loanType', e.target.value)}
                  >
                    <option value={LOAN_TYPES.MORTGAGE}>Mortgage</option>
                    <option value={LOAN_TYPES.HELOC}>HELOC</option>
                    <option value={LOAN_TYPES.HOME_EQUITY_LOAN}>Home Equity Loan</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Loan Amount</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      value={inputs.loanAmount}
                      onChange={(e) => handleInputChange('loanAmount', parseFloat(e.target.value))}
                      step="1000"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Estimated Property Value</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      value={inputs.propertyValue}
                      onChange={(e) => handleInputChange('propertyValue', parseFloat(e.target.value))}
                      step="1000"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <div className="row">
                    <div className="col-6">
                      <label className="form-label">LTV</label>
                      <input
                        type="text"
                        className="form-control"
                        value={`${ltv}%`}
                        disabled
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">CLTV</label>
                      <input
                        type="text"
                        className="form-control"
                        value={`${cltv}%`}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {inputs.loanPurpose === LOAN_PURPOSES.REFINANCE && (
                  <div className="mb-3">
                    <label className="form-label">Cash-Out Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        value={inputs.cashOutAmount}
                        onChange={(e) => handleInputChange('cashOutAmount', parseFloat(e.target.value))}
                        step="1000"
                      />
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Lock Period (Days)</label>
                  <select
                    className="form-select"
                    value={inputs.lockPeriod}
                    onChange={(e) => handleInputChange('lockPeriod', parseInt(e.target.value))}
                  >
                    <option value="15">15 Days</option>
                    <option value="30">30 Days</option>
                    <option value="45">45 Days</option>
                    <option value="60">60 Days</option>
                  </select>
                </div>

                <button
                  className="btn btn-primary w-100"
                  onClick={handleRunPricing}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Pricing...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-calculator me-1"></i>
                      Run Pricing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results & AI Insights */}
          <div className="col-lg-9">
            {pricingResults.length === 0 && !loading && (
              <div className="card">
                <div className="empty">
                  <div className="empty-icon">
                    <i className="ti ti-calculator"></i>
                  </div>
                  <p className="empty-title">Ready to Price</p>
                  <p className="empty-subtitle text-secondary">
                    Enter your pricing inputs and click "Run Pricing" to see available products and rates.
                  </p>
                </div>
              </div>
            )}

            {loading && (
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status"></div>
                  <div className="h3">Running Pricing Engine...</div>
                  <div className="text-secondary">Analyzing products and rates</div>
                </div>
              </div>
            )}

            {pricingResults.length > 0 && !loading && (
              <>
                {/* Eligible Products Summary */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h3 className="card-title">
                      Pricing Results ({pricingResults.filter(r => r.eligible).length} products)
                    </h3>
                    <div className="card-actions">
                      <div className="btn-list">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={filterEligible}
                            onChange={(e) => setFilterEligible(e.target.checked)}
                          />
                          <label className="form-check-label">Eligible Only</label>
                        </div>
                        <select
                          className="form-select form-select-sm"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="interestRate">Sort by Rate</option>
                          <option value="monthlyPayment">Sort by Payment</option>
                          <option value="productName">Sort by Name</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row row-cards">
                      {getSortedResults().map((result) => (
                        <div key={result.id} className="col-md-4">
                          <div 
                            className={`card ${selectedScenario?.id === result.id ? 'border-primary' : ''} ${result.eligible ? 'cursor-pointer' : ''}`}
                            onClick={() => result.eligible && setSelectedScenario(result)}
                            style={{ cursor: result.eligible ? 'pointer' : 'default' }}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h3 className="card-title mb-1">{result.productName}</h3>
                                  <div className="text-secondary small">{result.programType}</div>
                                  {result.term && (
                                    <div className="text-secondary small">
                                      {result.term / 12} years • {result.amortizationType}
                                    </div>
                                  )}
                                </div>
                                {result.eligible ? (
                                  <span className="badge bg-success">Eligible</span>
                                ) : (
                                  <span className="badge bg-danger">Ineligible</span>
                                )}
                              </div>
                              
                              {result.eligible ? (
                                <>
                                  <div className="row mb-2">
                                    <div className="col-6">
                                      <div className="text-secondary small">Rate</div>
                                      <div className="h3 mb-0">{result.interestRate.toFixed(3)}%</div>
                                    </div>
                                    <div className="col-6">
                                      <div className="text-secondary small">APR</div>
                                      <div className="h3 mb-0">{result.apr.toFixed(3)}%</div>
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="text-secondary small">Monthly Payment</div>
                                    <div className="h4 mb-0">${result.monthlyPayment.toLocaleString()}</div>
                                    {result.pmi > 0 && (
                                      <div className="text-secondary small">+${result.pmi.toFixed(2)} PMI</div>
                                    )}
                                  </div>
                                  <div className="btn-list mt-3">
                                    {selectedScenario?.id === result.id && (
                                      <button className="btn btn-primary btn-sm w-100">
                                        <i className="ti ti-check me-1"></i>
                                        Selected
                                      </button>
                                    )}
                                    {selectedScenario?.id !== result.id && (
                                      <button 
                                        className="btn btn-outline-primary btn-sm w-100"
                                        onClick={() => setSelectedScenario(result)}
                                      >
                                        View Details
                                      </button>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="text-secondary">
                                  {result.ineligibilityReasons?.join(', ') || 'Does not meet criteria'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Selected Product Details - Full Width Below */}
        {pricingResults.length > 0 && !loading && selectedScenario && selectedScenario.eligible && (
          <div 
            className="row mt-3" 
            ref={selectedDetailsRef}
            style={{
              marginRight: isAIPanelLocked ? '420px' : '0',
              transition: 'margin-right 0.3s ease'
            }}
          >
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-primary-lt">
                  <h3 className="card-title">Selected: {selectedScenario.productName}</h3>
                  <div className="card-actions">
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => setShowSaveModal(true)}
                    >
                      <i className="ti ti-device-floppy me-1"></i>
                      Save Scenario
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => setShowConvertModal(true)}
                    >
                      <i className="ti ti-arrow-right me-1"></i>
                      Create Loan
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h4 className="mb-3">Loan Details</h4>
                      <table className="table">
                        <tbody>
                          <tr>
                            <td>Interest Rate</td>
                            <td className="text-end fw-bold">{selectedScenario.interestRate.toFixed(3)}%</td>
                          </tr>
                          <tr>
                            <td>APR</td>
                            <td className="text-end">{selectedScenario.apr.toFixed(3)}%</td>
                          </tr>
                          <tr>
                            <td>Monthly P&I</td>
                            <td className="text-end fw-bold">${selectedScenario.monthlyPayment.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td>Term</td>
                            <td className="text-end">{selectedScenario.term / 12} years</td>
                          </tr>
                          <tr>
                            <td>Closing Costs</td>
                            <td className="text-end">${selectedScenario.closingCosts.toLocaleString()}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <h4 className="mb-3">Features</h4>
                      <ul>
                        {selectedScenario.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                      {selectedScenario.eligibilityNotes && (
                        <div className="alert alert-info mt-3">
                          <i className="ti ti-info-circle me-2"></i>
                          {selectedScenario.eligibilityNotes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparison View - Full Width Below */}
        {pricingResults.length > 0 && !loading && comparedScenarios.length > 0 && (
          <div 
            className="row mt-3"
            style={{
              marginRight: isAIPanelLocked ? '420px' : '0',
              transition: 'margin-right 0.3s ease'
            }}
          >
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Compare Scenarios ({comparedScenarios.length})</h3>
                  <div className="card-actions">
                    <button
                      className="btn btn-sm btn-ghost-secondary"
                      onClick={() => setComparedScenarios([])}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        {comparedScenarios.map(s => (
                          <th key={s.id} className="text-center">{s.productName}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fw-bold">Interest Rate</td>
                        {comparedScenarios.map(s => (
                          <td key={s.id} className="text-center">{s.interestRate.toFixed(3)}%</td>
                        ))}
                      </tr>
                      <tr>
                        <td className="fw-bold">Monthly Payment</td>
                        {comparedScenarios.map(s => (
                          <td key={s.id} className="text-center">${s.monthlyPayment.toLocaleString()}</td>
                        ))}
                      </tr>
                      <tr>
                        <td className="fw-bold">Term</td>
                        {comparedScenarios.map(s => (
                          <td key={s.id} className="text-center">{s.term / 12} years</td>
                        ))}
                      </tr>
                      <tr>
                        <td className="fw-bold">Total Interest</td>
                        {comparedScenarios.map(s => (
                          <td key={s.id} className="text-center">
                            ${((s.monthlyPayment * s.term) - inputs.loanAmount).toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Save Scenario</h5>
                <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Scenario Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={scenarioName}
                    onChange={(e) => setScenarioName(e.target.value)}
                    placeholder="e.g., Smith Purchase - Conv 30yr"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-link link-secondary" onClick={() => setShowSaveModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveScenario} disabled={!scenarioName}>
                  <i className="ti ti-device-floppy me-1"></i>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Loan Modal */}
      {showConvertModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Loan from Scenario</h5>
                <button type="button" className="btn-close" onClick={() => setShowConvertModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Selected Product:</strong> {selectedScenario.productName}<br />
                  <strong>Rate:</strong> {selectedScenario.interestRate.toFixed(3)}%<br />
                  <strong>Payment:</strong> ${selectedScenario.monthlyPayment.toLocaleString()}/mo
                </div>

                <p className="text-secondary mb-3">
                  Choose how to create the loan with this pricing locked in:
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="card card-link card-link-pop cursor-pointer" onClick={() => handleConvertToLoan(true)}>
                      <div className="card-body text-center">
                        <div className="mb-3">
                          <i className="ti ti-file-plus" style={{ fontSize: '3rem', color: 'var(--tblr-primary)' }}></i>
                        </div>
                        <h3 className="card-title mb-2">Manual Entry</h3>
                        <p className="text-secondary">
                          Enter borrower information manually. Pricing will be locked in.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className={`card card-link card-link-pop ${!mismoFile ? 'opacity-50' : 'cursor-pointer'}`}
                      onClick={() => mismoFile && handleConvertToLoan(false)}
                    >
                      <div className="card-body text-center">
                        <div className="mb-3">
                          <i className="ti ti-file-import" style={{ fontSize: '3rem', color: 'var(--tblr-success)' }}></i>
                        </div>
                        <h3 className="card-title mb-2">MISMO Merge</h3>
                        <p className="text-secondary">
                          Use imported MISMO file with selected pricing. No data loss.
                        </p>
                        {!mismoFile && (
                          <div className="badge bg-yellow-lt">No MISMO file imported</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-link link-secondary" onClick={() => setShowConvertModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating AI Assistant */}
      <FloatingAIAssistant 
        activeTab="scenarioPricer"
        loan={null}
        onAnalyze={(analysis) => {
          console.log('AI Analysis:', analysis);
        }}
        onLockChange={setIsAIPanelLocked}
      />
    </>
  );
};

export default ScenarioPricerPage;
